import { useEffect, useState } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { isLocal } from './env-utils';

const JIRA_SUPPORT_FLAG = 'jira-support-v1';

// PostHog resolves an unknown flag to false as soon as flags load, so this only elapses when the
// request itself fails or is blocked — and a user PostHog can't reach can't be in a rollout anyway.
const FLAG_RESOLUTION_TIMEOUT_MS = 2000;

export function useFlagEnabled(name: string) {
  const enabled = useFeatureFlagEnabled(name);
  return isLocal || enabled;
}

export function useJiraSupportEnabled() {
  return useFlagEnabled(JIRA_SUPPORT_FLAG);
}

export function useIntercomEnabled() {
  const jiraSupport = useFeatureFlagEnabled(JIRA_SUPPORT_FLAG);
  const resolutionTimedOut = useFlagResolutionTimeout();

  if (isLocal) return false;
  // Everywhere else an unresolved flag can safely read as "off" — the feature just appears a beat
  // late. Not here: IntercomProvider injects the widget on the first render that initializes it
  // and latches, with no way to undo it, so reading "not yet known" as "off" would boot Intercom
  // for users the flag is on for. Wait for the real value instead.
  if (jiraSupport === undefined) return resolutionTimedOut;
  return !jiraSupport;
}

export function useAssistantEnabled() {
  return useFlagEnabled('bravo-assistant-v1');
}

export function useWrkthruEnabled() {
  // Wrkthru requires the Assistant: every "Wrkthru It" launches it, so the page is
  // non-functional without it. Call both hooks unconditionally, then combine the values —
  // inlining as `useFlagEnabled('wrkthru-v1') && useAssistantEnabled()` would skip the
  // second hook when the first is false and break the Rules of Hooks.
  const wrkthruFlag = useFlagEnabled('wrkthru-v1');
  const assistantEnabled = useAssistantEnabled();
  return wrkthruFlag && assistantEnabled;
}

export function useWrkthruSwitcherEnabled() {
  // The switcher rides on top of Wrkthru itself, so both flags must be on.
  const wrkthruEnabled = useWrkthruEnabled();
  const switcherFlag = useFlagEnabled('wrkthru-switcher-v1');
  return wrkthruEnabled && switcherFlag;
}

function useFlagResolutionTimeout() {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), FLAG_RESOLUTION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  return timedOut;
}
